import React from 'react'

import { Flex, Text } from 'govwell-ui'

type Props = {
  iconUrl?: string
  label?: string
}

export const ExternalSitePreview = ({ iconUrl, label }: Props) => (
  <div className="flex w-full flex-col overflow-hidden rounded-lg border border-neutral-200 shadow-sm">
    <div className="border-b border-neutral-200 bg-neutral-100 px-3 py-1.5">
      <Text.BodySmall className="font-medium text-neutral-500">
        {label ?? 'External Website'}
      </Text.BodySmall>
    </div>
    <div className="relative h-56 bg-neutral-50">
      {/* Dark header bar */}
      <div className="flex items-center bg-[#1a3a5c] px-3 py-2">
        <span className="text-[10px] font-semibold text-white">YourOrg.com</span>
      </div>
      {/* Nav bar */}
      <div className="flex gap-3 bg-[#244a6e] px-3 py-1.5">
        <div className="h-1.5 w-8 rounded bg-white/40" />
        <div className="h-1.5 w-10 rounded bg-white/40" />
        <div className="h-1.5 w-12 rounded bg-white/40" />
        <div className="h-1.5 w-9 rounded bg-white/40" />
      </div>
      {/* Hero area */}
      <div className="bg-gradient-to-br from-[#1a3a5c] to-[#2d6a9f] px-3 py-4 text-center">
        <span className="text-[10px] font-semibold text-white">Your Org&apos;s Website</span>
      </div>
      {/* Content cards */}
      <div className="flex gap-2 px-3 py-3">
        <div className="flex-1 rounded border border-neutral-200 bg-white p-2">
          <div className="mb-1 h-1.5 w-12 rounded bg-neutral-700" />
          <div className="h-1 w-full rounded bg-neutral-200" />
        </div>
        <div className="flex-1 rounded border border-neutral-200 bg-white p-2">
          <div className="mb-1 h-1.5 w-14 rounded bg-neutral-700" />
          <div className="h-1 w-full rounded bg-neutral-200" />
        </div>
        <div className="flex-1 rounded border border-neutral-200 bg-white p-2">
          <div className="mb-1 h-1.5 w-10 rounded bg-neutral-700" />
          <div className="h-1 w-full rounded bg-neutral-200" />
        </div>
      </div>
      {/* Launcher icon */}
      <div className="absolute right-3 bottom-3">
        {iconUrl ? (
          <img
            src={iconUrl}
            alt="Launcher preview"
            className="h-10 w-10 rounded-full object-cover shadow-lg ring-1 ring-black/10"
          />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-500 shadow-lg ring-1 ring-black/10">
            <div className="h-4 w-4 rounded bg-white/80" />
          </div>
        )}
      </div>
    </div>
  </div>
)

export const PublicPortalPreview = ({ iconUrl, label }: Props) => (
  <div className="flex w-full flex-col overflow-hidden rounded-lg border border-neutral-200 shadow-sm">
    <div className="border-b border-neutral-200 bg-neutral-100 px-3 py-1.5">
      <Text.BodySmall className="font-medium text-neutral-500">
        {label ?? 'Public Portal'}
      </Text.BodySmall>
    </div>
    <div className="flex h-56 flex-col bg-white">
      {/* Chat header with icon */}
      <div className="flex items-center gap-2 border-b border-neutral-200 bg-white px-3 py-2">
        {iconUrl ? (
          <img
            src={iconUrl}
            alt="Portal header preview"
            className="h-8 w-8 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-500">
            <div className="h-3 w-3 rounded bg-white/80" />
          </div>
        )}
        <div className="flex flex-col">
          <span className="text-[11px] font-semibold text-neutral-900">
            Get Instant Answers with AI
          </span>
          <span className="text-[9px] text-neutral-500">
            AI can make mistakes. Double-check important details.
          </span>
        </div>
      </div>
      {/* Chat messages */}
      <div className="flex flex-1 flex-col gap-2 overflow-hidden p-3">
        <div className="self-start rounded-lg bg-neutral-100 px-2.5 py-1.5">
          <div className="mb-1 h-1.5 w-32 rounded bg-neutral-300" />
          <div className="h-1.5 w-24 rounded bg-neutral-300" />
        </div>
        <div className="self-start rounded-lg bg-neutral-100 px-2.5 py-1.5">
          <div className="mb-1 h-1.5 w-28 rounded bg-neutral-300" />
          <div className="h-1.5 w-20 rounded bg-neutral-300" />
        </div>
      </div>
      {/* Input bar */}
      <div className="border-t border-neutral-200 px-3 py-2">
        <div className="flex items-center rounded border border-neutral-200 px-2 py-1.5">
          <span className="text-[9px] text-neutral-400">Type here...</span>
          <div className="ml-auto h-3 w-3 rounded bg-orange-500" />
        </div>
      </div>
    </div>
  </div>
)

export const InProductPreview = ({ iconUrl, label }: Props) => (
  <div className="flex w-full flex-col overflow-hidden rounded-lg border border-neutral-200 shadow-sm">
    <div className="border-b border-neutral-200 bg-neutral-100 px-3 py-1.5">
      <Text.BodySmall className="font-medium text-neutral-500">
        {label ?? 'In-Product (Side Panel)'}
      </Text.BodySmall>
    </div>
    <div className="flex h-56 bg-white">
      {/* Sidebar */}
      <div className="flex w-[52px] flex-col bg-neutral-900 px-1.5 py-2">
        <span className="mb-2 text-[10px] font-extrabold text-white">GovWell</span>
        <div className="mb-3 h-1 w-8 rounded bg-white/30" />
        <div className="mb-1.5 h-1 w-7 rounded bg-white/40" />
        <div className="mb-1.5 h-1 w-9 rounded bg-white/40" />
        <div className="mb-1.5 h-1 w-6 rounded bg-white/40" />
        <div className="mb-1.5 h-1 w-8 rounded bg-orange-400/80" />
        <div className="mb-1.5 h-1 w-7 rounded bg-white/40" />
      </div>
      {/* Record page */}
      <div className="flex flex-1 flex-col border-r border-neutral-200">
        <div className="border-b border-neutral-100 px-2 py-1.5">
          <span className="text-[8px] font-semibold text-neutral-800">BP-2025-0042</span>
          <div className="mt-0.5 h-1.5 w-12 rounded bg-blue-200" />
        </div>
        <div className="flex gap-1.5 border-b border-neutral-100 px-2 py-1">
          <span className="text-[6px] text-neutral-500">Details</span>
          <span className="text-[6px] text-neutral-500">Tasks</span>
          <span className="text-[6px] text-neutral-500">Files</span>
        </div>
        <div className="p-2">
          <span className="text-[6px] font-medium text-neutral-700">Applicant</span>
          <div className="mt-0.5 mb-1.5 h-1 w-16 rounded bg-neutral-200" />
          <span className="text-[6px] font-medium text-neutral-700">Address</span>
          <div className="mt-0.5 h-1 w-20 rounded bg-neutral-200" />
        </div>
      </div>
      {/* Right: AICA side panel */}
      <div className="flex w-[45%] flex-col bg-white">
        {/* Panel header */}
        <div className="flex items-center gap-1.5 border-b border-neutral-200 px-2 py-2">
          {iconUrl ? (
            <img
              src={iconUrl}
              alt="Side panel header preview"
              className="h-5 w-5 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-500">
              <div className="h-2 w-2 rounded bg-white/80" />
            </div>
          )}
          <div className="flex flex-col">
            <span className="text-[9px] font-semibold text-neutral-900">Org Assistant</span>
            <span className="text-[7px] text-neutral-500">Powered by GovWell</span>
          </div>
        </div>
        {/* Empty state with icon */}
        <div className="flex flex-1 flex-col items-center justify-center gap-1.5 px-2">
          {iconUrl ? (
            <img
              src={iconUrl}
              alt="Empty state preview"
              className="h-10 w-10 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
              <div className="h-4 w-4 rounded bg-blue-400" />
            </div>
          )}
          <span className="text-[8px] font-semibold text-neutral-900">Org Assistant</span>
          <span className="text-center text-[7px] text-neutral-500">
            Ask about permits, forms, and requirements.
          </span>
        </div>
        {/* Input area */}
        <div className="border-t border-neutral-200 px-2 py-1.5">
          <div className="rounded border border-neutral-200 px-2 py-1">
            <span className="text-[8px] text-neutral-400">Ask anything...</span>
          </div>
          <div className="mt-1 flex gap-1">
            <div className="rounded-full border border-neutral-200 px-1.5 py-0.5">
              <span className="text-[7px] text-neutral-500">Help me find a form</span>
            </div>
            <div className="rounded-full border border-neutral-200 px-1.5 py-0.5">
              <span className="text-[7px] text-neutral-500">Look up permits</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
)

const IconPreviews = ({ iconUrl }: Props) => {
  return (
    <Flex direction="column" gap={3} className="w-full">
      <Text.BodySmall className="font-medium text-neutral-600">Preview</Text.BodySmall>
      <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3">
        <ExternalSitePreview iconUrl={iconUrl} />
        <PublicPortalPreview iconUrl={iconUrl} />
        <InProductPreview iconUrl={iconUrl} />
      </div>
    </Flex>
  )
}

export default React.memo(IconPreviews)
